// CustomCodeSection.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import EditIcon from '../../../../assets/icons/EditIcon';
import { font, Theme } from '../../../../utils/themes';
import { useTheme } from '../../../../middleware/ThemeContext';
import StyledText from '../../../../components/StyledComponents/StyledText';
import SecTitleWithIconDesc from '../../components/secTitleWithIconDesc';
import CodeIcon from '../../../../assets/icons/CodeIcon';
import { WebsiteFormValues } from '../../../../types/types';
import { SkeletonCell } from '../../../../components/SkeletonCell';
import CloseIcon from '../../../../assets/icons/CloseIcon';

// Main Container
const SectionContainer = styled.div<{ theme: Theme }>`
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 10px;
  padding: 35px 35px;
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
`;


// Code Block
const CodeBlock = styled.div<{ $isEditing?: boolean, theme: Theme }>`
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 10px;
  margin-top: 0.5rem;
  position: relative;


  ${({ $isEditing, theme }) =>
        $isEditing &&
        `    background-color:  ${theme.CorporateTheme.colors.background};
  `}
`;




// Textarea Style
const CodeTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  border: none;
  background: transparent;
  resize: vertical;
//   font-family: 'Courier New', Courier, monospace;
  font-family: ${font.raleway}

  font-size: 16px;
  line-height: 1.5;
//   color: #111827;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};

padding: 15px 25px;
  outline: none;

  &::placeholder {
    color: #6b7280;
    opacity: 1;
  }
`;

const Header = styled.div<{ theme: Theme }>`
background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
border-radius:10px 10px 0px 0px;
display: flex;
align-items: center;
justify-content: space-between;
padding: 10px 25px;
`

interface CustomCodeSectionProps {
    headTagValue?: string;
    customCssValue?: string;
    customJsValue?: string;
    onHeadTagChange?: (value: string) => void;
    onCssChange?: (value: string) => void;
    onJsChange?: (value: string) => void;
    isResponsive?: boolean;
    setSettingConfiguration: React.Dispatch<React.SetStateAction<WebsiteFormValues>>, settingConfiguration: WebsiteFormValues;
    companyLoading?: boolean
}

const CustomCodeSection: React.FC<CustomCodeSectionProps> = ({

    isResponsive,
    setSettingConfiguration, settingConfiguration, companyLoading
}) => {
    const { theme } = useTheme();
    const [editing, setEditing] = useState<null | 'head' | 'css' | 'js'>(null);

    const handleEditClick = (section: 'head' | 'css' | 'js') => {
        setEditing(section);
    };

    const handleBlur = () => {
        setEditing(null);
    };

    const getRandomWidth = () => {
        const min = 120;
        const max = 260;
        return `${Math.floor(Math.random() * (max - min + 1)) + min}px`;
    };

    return (
        <SectionContainer theme={theme}>

            <SecTitleWithIconDesc fontWeight={700} fontSize={isResponsive ? '16px' : '18px'} fontColor={theme.CorporateTheme.colors.primary} descfontSize={isResponsive ? '12px' : '16px'} icon={<CodeIcon size={isResponsive ? 22 : 32} color={theme.CorporateTheme.colors.primary} />} title='Custom Code' desc='Inject custom CSS or HTML to enhance your website functionality' />

            {/* HEAD Tag Section */}
            <CodeBlock $isEditing={editing === 'head'} theme={theme} className='mt-4'>
                <Header theme={theme}>
                    <StyledText fontSize={isResponsive ? '14px' : '16px'} fontWeight={600}>HEAD Tag</StyledText>
                    <div className='cursor-pointer'>
                        {editing === 'head' ? <CloseIcon size={isResponsive ? 20 : 24} color={theme.CorporateTheme.colors.headerColor} onClick={() => setEditing(null)} /> : <EditIcon size={isResponsive ? 20 : 24} color={theme.CorporateTheme.colors.headerColor} onClick={() => handleEditClick('head')} />}
                    </div>
                </Header>
                {companyLoading ?
                    <div className='p-4 d-flex flex-column gap-2'>
                        {Array.from({ length: 5 }).map((_, index) => (
                            <SkeletonCell key={index} width={getRandomWidth()}
                                height={'15px'} />
                        ))
                        }
                    </div>

                    : <CodeTextarea
                        value={settingConfiguration.headerHtml}
                        onChange={(e) => setSettingConfiguration({ ...settingConfiguration, headerHtml: e.target.value })}
                        onBlur={handleBlur}
                        placeholder="<HEAD>\n\n</HEAD>"
                        rows={5}
                        disabled={editing !== 'head'}
                        theme={theme}
                    />}
            </CodeBlock>

            {/* Custom CSS Section */}
            <CodeBlock $isEditing={editing === 'css'} theme={theme}>
                <Header theme={theme}>
                    <StyledText fontSize={isResponsive ? '14px' : '16px'} fontWeight={600}>Custom CSS</StyledText>
                    <div  className='cursor-pointer'>
                        {editing === 'css' ? <CloseIcon size={isResponsive ? 20 : 24} color={theme.CorporateTheme.colors.headerColor} onClick={() => setEditing(null)}/> : <EditIcon onClick={() => handleEditClick('css')} size={isResponsive ? 20 : 24} color={theme.CorporateTheme.colors.headerColor} />}
                    </div>
                </Header>
                {companyLoading ?
                    <div className='p-4 d-flex flex-column gap-2'>
                        {Array.from({ length: 5 }).map((_, index) => (
                            <SkeletonCell key={index} width={getRandomWidth()}
                                height={'15px'} />
                        ))
                        }
                    </div>

                    : <CodeTextarea
                        value={settingConfiguration.customCss}
                        onChange={(e) => setSettingConfiguration({ ...settingConfiguration, customCss: e.target.value })}
                        onBlur={handleBlur}
                        placeholder="/* Add your custom CSS here */"
                        rows={5}
                        disabled={editing !== 'css'}
                        theme={theme}
                    />}
            </CodeBlock>

            {/* Custom JS Section */}
            <CodeBlock $isEditing={editing === 'js'} theme={theme}>
                <Header theme={theme}>
                    <StyledText fontSize={isResponsive ? '14px' : '16px'} fontWeight={600}>Custom JS</StyledText>
                    <div  className='cursor-pointer'>
                        {editing === 'js' ? <CloseIcon size={isResponsive ? 20 : 24} color={theme.CorporateTheme.colors.headerColor} onClick={() => setEditing(null)} /> : <EditIcon onClick={() => handleEditClick('js')} size={isResponsive ? 20 : 24} color={theme.CorporateTheme.colors.headerColor} />}
                    </div>
                </Header>
                {companyLoading ?
                    <div className='p-4 d-flex flex-column gap-2'>
                        {Array.from({ length: 5 }).map((_, index) => (
                            <SkeletonCell key={index} width={getRandomWidth()}
                                height={'15px'} />
                        ))
                        }
                    </div>

                    : <CodeTextarea
                        value={settingConfiguration.customJs}
                        onChange={(e) => setSettingConfiguration({ ...settingConfiguration, customJs: e.target.value })}
                        onBlur={handleBlur}
                        placeholder="<!-- Custom JAVASCRIPT Code for -->"
                        rows={5}
                        disabled={editing !== 'js'}
                        theme={theme}
                    />}
            </CodeBlock>
        </SectionContainer>
    );
};

export default CustomCodeSection;